import { debounce } from 'lodash'
import { makeAutoObservable, runInAction } from 'mobx'
import { isCancel } from 'axios'
import { type IDropdownItem, EnumDropdownItemVariant } from 'shared/ui'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { InboxesApi } from 'entities/Inbox'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { UsersApi } from 'entities/Users'

const getSmartOptionLabel = (key: 'scaler' | 'local_presence') =>
  key === 'scaler' ? 'Scaler' : 'Local presence'

export class DefaultInboxStore {
  defaultInboxId: number | null = null
  defaultInboxName: string | null = null
  defaultInboxNumber: string | null = null
  defaultInboxNumberId: number | null = null
  defaultInboxSmartOption: 'scaler' | 'local_presence' | null = null

  private _loading = false
  private _loadingMore = false
  private _itemsMap: Map<number | string, IDropdownItem> = new Map()
  private _selectedChildId: string | null = null
  private _search = ''
  private _page = 1
  private _limit = 20
  private _total = 0
  private _requestCancelAxios = new RequestCancelAxios()
  private _debounceLoad: ReturnType<typeof debounce>

  constructor() {
    this._debounceLoad = debounce(this._doLoad, 500)
    makeAutoObservable(this)
  }

  updateDefaultInbox = async (
    id: number,
    numberId: number | null = null,
    smartOption: 'scaler' | 'local_presence' | null = null
  ) => {
    try {
      runInAction(() => {
        this._loading = true
      })

      await UsersApi.updateCallingWidgetDefaultInbox({
        team_id: id,
        number_id: numberId,
        smart_option: smartOption,
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  getDefaultInbox = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await UsersApi.getCallingWidgetDefaultInbox()

      return data
    } catch (e) {
      logger.error(e)

      return null
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  init = async () => {
    const defaultInboxData = await this.getDefaultInbox()

    runInAction(() => {
      this.defaultInboxId = defaultInboxData?.team_id ?? null
    })

    if (this.defaultInboxId) {
      try {
        const { data } = await InboxesApi.getTeamById(this.defaultInboxId)

        const numberId = defaultInboxData?.number_id
        const smartOption = defaultInboxData?.smart_option

        const matchedNumber = numberId
          ? (data.numbers ?? []).find((number) => number.id === numberId)
          : null

        runInAction(() => {
          this.defaultInboxName = data.name
          this._selectedChildId = null
          this.defaultInboxNumber = null
          this.defaultInboxNumberId = null
          this.defaultInboxSmartOption = null

          if (numberId) {
            this._selectedChildId = `${this.defaultInboxId}_${numberId}`
            this.defaultInboxNumber = matchedNumber?.formatted_number ?? null
            this.defaultInboxNumberId = numberId
          } else if (smartOption) {
            this._selectedChildId = `${this.defaultInboxId}_${smartOption}`
            this.defaultInboxSmartOption = smartOption
          }
        })
      } catch (e) {
        logger.error(e)
      }
    }
  }

  setDefaultInbox = (
    id: number,
    name: string,
    selectedChildId: string | null = null,
    number: string | null = null,
    numberId: number | null = null,
    smartOption: 'scaler' | 'local_presence' | null = null
  ) => {
    this.defaultInboxId = id
    this.defaultInboxName = name
    this._selectedChildId = selectedChildId
    this.defaultInboxNumber = number
    this.defaultInboxNumberId = numberId
    this.defaultInboxSmartOption = smartOption
    this.updateDefaultInbox(id, numberId, smartOption)
  }

  loadInboxes = async () => {
    runInAction(() => {
      this._loading = true
      this._itemsMap.clear()
      this._page = 1
      this._search = ''
    })
    await this._doLoad()
  }

  private _doLoad = async () => {
    try {
      this._requestCancelAxios.init()
      const response = await InboxesApi.getTeamsSearch(
        {
          page: this._page,
          per_page: this._limit,
          by_all_members: false,
          ...(this._search ? { term: this._search } : {}),
        },
        { cancelToken: this._requestCancelAxios.token }
      )
      const { results, total } = response.data

      runInAction(() => {
        this._total = total
        this._itemsMap.clear()
        this._addItems(results)
        this._loading = false
      })
    } catch (e) {
      if (!isCancel(e)) {
        logger.error(e)
        runInAction(() => {
          this._loading = false
        })
      }
    }
  }

  loadMore = async () => {
    if (this._loadingMore || !this.isMore) return

    runInAction(() => {
      this._loadingMore = true
    })

    try {
      this._requestCancelAxios.init()
      const response = await InboxesApi.getTeamsSearch(
        {
          page: this._page + 1,
          per_page: this._limit,
          by_all_members: false,
          ...(this._search ? { term: this._search } : {}),
        },
        { cancelToken: this._requestCancelAxios.token }
      )
      const { results, total } = response.data

      runInAction(() => {
        this._page += 1
        this._total = total
        this._addItems(results)
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingMore = false
      })
    }
  }

  private _addItems = (results: IResponseInbox[]) => {
    results.forEach((inbox) => {
      const numbers = inbox.numbers ?? []
      const sendingOptions = inbox.sending_options?.smart ?? []

      const filteredNumbers = numbers.filter((number) => {
        if (number.is_aircall) return false
        if (Boolean(number.short_code)) return false
        return !(number.is_toll_free && number.verified_status !== 'Verified')
      })

      const children: IDropdownItem[] = [
        ...sendingOptions.map(({ key, number_ids }) => ({
          id: `${inbox.id}_${key}`,
          label: getSmartOptionLabel(key),
          labelRight: `${number_ids.length} numbers`,
          data: {
            inboxId: inbox.id,
            inboxName: inbox.name,
            smartOptionLabel: getSmartOptionLabel(key),
            smartOptionKey: key,
          },
          variant: EnumDropdownItemVariant.CheckedRight,
          noRenderEmptyActiveValue: true,
        })),
        ...filteredNumbers.map((number) => ({
          id: `${inbox.id}_${number.id}`,
          label: number.formatted_number,
          data: {
            inboxId: inbox.id,
            inboxName: inbox.name,
            numberFormatted: number.formatted_number,
            numberId: number.id,
          },
          variant: EnumDropdownItemVariant.CheckedRight,
          noRenderEmptyActiveValue: true,
        })),
      ]

      if (!children.length) return

      const hasSendingOptions = sendingOptions.length > 0
      const childrenDropdownWidth = hasSendingOptions ? 256 : 162

      this._itemsMap.set(inbox.id, {
        id: inbox.id,
        label: inbox.name,
        iconL: 'inboxes2',
        variant: EnumDropdownItemVariant.Default,
        isCardClick: children.length <= 1,
        tooltipLabelPlacement: 'top-start',
        data:
          children.length === 1 ? children[0].data : { inboxId: inbox.id, inboxName: inbox.name },
        children: children.length > 0 ? children : undefined,
        iconR: children.length > 1 ? 'chevronRight' : undefined,
        childrenDropdownProps:
          children.length > 0
            ? {
                placement: 'left-start',
                margin: 0,
                marginTop: -3,
                width: childrenDropdownWidth,
                TransitionProps: { timeout: 100 },
                PopperProps: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -childrenDropdownWidth],
                      },
                    },
                    {
                      name: 'preventOverflow',
                      options: { altAxis: true },
                    },
                  ],
                },
              }
            : undefined,
      })
    })
  }

  onSearch = (value: string) => {
    this._requestCancelAxios.reset()
    this._debounceLoad?.cancel?.()

    runInAction(() => {
      this._search = value
      this._loading = true
      this._page = 1
      this._itemsMap.clear()
    })

    this._debounceLoad()
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get loadingMore() {
    return this._loadingMore
  }

  get isMore() {
    return Math.ceil(this._total / this._limit) > this._page
  }

  get dropdownItems(): IDropdownItem[] {
    return Array.from(this._itemsMap.values()).map((item) => {
      if (item.id !== this.defaultInboxId) return item

      const activeChild = this._selectedChildId
        ? item.children?.find((child) => child.id === this._selectedChildId)
        : null

      return {
        ...item,
        activeValue: this.defaultInboxNumber ?? activeChild?.label ?? true,
        children: item.children?.map((child) => ({
          ...child,
          activeValue: child.id === this._selectedChildId,
        })),
      }
    })
  }
}
